import React, {useEffect, useRef, useState} from 'react'
import InstitutionResource from 'src/entities/InstitutionResource'
import {useForm, FormProvider} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import {Box, Button, Stack, CircularProgress, Alert} from '@mui/material'
import schema from 'src/components/form/schema'
import ContactDetails from 'src/components/form/ContactDetails'
import InstitutionDetails from 'src/components/form/InstitutionDetails'
import BankDetails, {Bank} from 'src/components/form/BankDetails'
import PaymentMethod from 'src/components/form/PaymentMethod'
import Attachments, {AttachmentsHandle} from 'src/components/form/Attachments'
import Commitment from 'src/components/form/Commitment'
import AssociationDetails from 'src/components/form/AssociationDetails'
import config from 'src/config'
import {AutocompleteOption} from 'src/components/ui/AutoComplete'
import {CodeTableEnum} from 'src/enum/lists'
import {fetchList, fetchData} from 'src/components/FormWrapper'
import RefreshPopup from 'src/components/ui/RefreshPopup'
import UploadDocument from 'src/entities/UploadDocument'
import {dataUrlToFile, getFileExtension} from 'src/utils/file'
import {useNavigate} from 'react-router-dom'
import {takeScreenshotAsImage} from 'src/utils/Screenshot'
import {IndificationType} from 'src/enum/indificationType'

const validationSchema: any = schema

const MyForm = ({
  institutionData
}: {
  institutionData: InstitutionResource | null
}) => {
  const resolver = yupResolver(validationSchema)
  const [cities, setCities] = useState<AutocompleteOption[]>([])
  const [indificationTypes, setIndificationTypes] = useState<any[]>([])
  const [banks, setBanks] = useState<Bank[]>([])
  const [initDataFaild, setInitDataFaild] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [submitError, setSubmitError] = useState<boolean>(false)
  const attachmentsRef = useRef<AttachmentsHandle>(null)

  const methods = useForm({
    resolver,
    mode: 'onChange',
    defaultValues: {
      isAssociationBelongTo: true,
      stipendCalendarType: 1,
      receivesDatotBudget: false,
      contact: {
        indificationTypeID: ''
      }
    }
  })

  useEffect(() => {
    if (!institutionData || !indificationTypes.length) return
    const d: InstitutionResource = institutionData
    methods.reset({
      ...d,
      stipendCalendarType: 1,
      receivesDatotBudget: false,
      contact: {
        ...d.contact,
        indificationTypeID:
          d.contact?.indificationTypeID ?? IndificationType.IndificationNo
      },
      association: {
        ...d.association,
        associationId: d.association?.associationId ?? ''
      }
    })
  }, [institutionData, indificationTypes, methods])

  const navigate = useNavigate()

  const initCities = () => {
    fetch(`${config.apiCityUrl}`)
      .then(response => response.json())
      .then((result: any) => {
        const cities = result.map((c: any) => ({
          id: c.nameEn ?? '',
          label: c.name
        }))
        setCities(cities)
        return cities
      })
      .catch(() => setInitDataFaild(true))
  }

  const initBanks = () => {
    fetchData(`${config.apiUrl}/CodeTables/bank`)
      .then((res: Bank[]) => setBanks(res))
      .catch(() => setInitDataFaild(true))
  }

  const initIndificationTypes = () => {
    fetchList(CodeTableEnum.IndificationTypes)
      .then(async (res: any) => {
        if (res?.length) setIndificationTypes(res)
        else setInitDataFaild(true)
      })
      .catch((e: any) => console.log(e))
  }

  const onSubmit = async (formData: any, signature: File) => {
    setIsLoading(true)
    try {
      const response: any = await fetch(`${config.apiUrl}/Institutions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const formId = await response.json()
      uploadAttachments(formId, signature)
      console.log('Data saved:', formId)
    } catch (error) {
      console.error('Error saving data:', error)
      setIsLoading(false)
      setSubmitError(true)
    }
  }

  const currentMonth = new Date().getMonth() + 1
  const currentYear = new Date().getFullYear()

  const uploadAttachments = async (formId: number, signature: File) => {
    const screenShot = await takeScreenshotAsImage()
    const screenshotFile: File = dataUrlToFile(screenShot, 'screenshot.png')

    const uploadPromises = []
    uploadPromises.push(
      uploadDocument(
        {
          InstitutionId: formId,
          CategoryId: 7,
          Name: signature.name,
          Extension: getFileExtension(signature.name),
          Month: currentMonth,
          Year: currentYear
        } as UploadDocument,
        signature
      )
    )

    uploadPromises.push(
      uploadDocument(
        {
          InstitutionId: formId,
          CategoryId: 13,
          Name: screenshotFile.name,
          Extension: getFileExtension(signature.name),
          Month: currentMonth,
          Year: currentYear
        } as UploadDocument,
        screenshotFile
      )
    )

    const attachments = attachmentsRef.current?.getAttachments() ?? []
    attachments.forEach(attachment => {
      if (attachment.File) {
        uploadPromises.push(
          uploadDocument(
            {
              InstitutionId: formId,
              CategoryId: attachment.CategoryId,
              Name: attachment.File.name,
              Extension: getFileExtension(attachment.File.name),
              Month: attachment.Month === -1 ? currentMonth : attachment.Month,
              Year: attachment.Year || currentYear
            } as UploadDocument,
            attachment.File,
            attachment.Id
          )
        )
      }
    })
    const results = await Promise.all(uploadPromises)
    const failedDocuments = results.filter(r => r !== undefined)
    if (failedDocuments.length > 0)
      navigate(`/error/${formId}`, {state: {failedDocuments}})
    else navigate(`/success/${formId}`)
  }

  const uploadDocument = async (
    documentData: UploadDocument,
    file: File,
    id?: number
  ) => {
    if (id) {
      attachmentsRef.current?.setUploading(id, true)
    }
    const formData = new FormData()
    formData.append('InstitutionId', documentData.InstitutionId.toString())
    formData.append('Name', documentData.Name)
    formData.append('CategoryId', documentData.CategoryId.toString())
    formData.append('Extension', documentData.Extension)
    formData.append('Month', documentData?.Month?.toString() || '')
    formData.append('Year', documentData?.Year?.toString())
    formData.append('file', file, file.name)

    for (let i = 0; i < 2; i++) {
      try {
        const response = await fetch(`${config.apiUrl}/Documents/Upload`, {
          method: 'POST',
          body: formData
        })
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        return
      } catch (error) {
        if (i === 1) {
          return file.name
        }
      }
    }
    if (id) {
      attachmentsRef.current?.setUploading(id, false)
    }
  }

  const isMissingFile = () => {
    const attachments = attachmentsRef.current?.getAttachments() ?? []
    return attachments.some(a => a.IsRequierd && !a.File)
  }

  const validateAndSubmit = async (formData: any) => {
    const data = {...formData}
    data.stipendPaymentDay = Number(data.stipendPaymentDay)

    if (isMissingFile()) return

    const signature: File = dataUrlToFile(
      data.signature,
      'חתימה על טופס בקשה.png'
    )

    delete data.signature
    delete data.isCommitmentConfirmation
    delete data.associationId
    delete data.association.id
    await onSubmit(data, signature)
  }

  useEffect(() => {
    initCities()
    initIndificationTypes()
    initBanks()
  }, [])

  return (
    <>
      <FormProvider {...methods}>
        <form dir="rtl" onSubmit={methods.handleSubmit(validateAndSubmit)}>
          <Box sx={{pointerEvents: isLoading ? 'none' : undefined}}>
            <Stack direction="column" spacing={2}>
              <ContactDetails
                cities={cities}
                indificationTypes={indificationTypes}
              />
              <InstitutionDetails cities={cities} />
              <AssociationDetails cities={cities} />
              <BankDetails banks={banks} />
              <PaymentMethod />
              <Attachments ref={attachmentsRef} />
              <Commitment />
              <Stack sx={{padding: 2}}>
                <Button
                  style={{
                    fontSize: 'large',
                    width: '120px',
                    alignSelf: 'end',
                    padding: '16px',
                    backgroundColor: isLoading ? '#9e9e9e' : '#4d0026',
                    color: 'white',
                    fontWeight: 'bold',
                    borderRadius: '15px'
                  }}
                  type="submit"
                  onClick={async () => {}}
                  disabled={isLoading}
                >
                  שלח
                  {isLoading && (
                    <CircularProgress
                      size={35}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        fontWeight: 'bold',
                        marginTop: -2,
                        marginLeft: -2
                      }}
                    />
                  )}
                </Button>
                {submitError && (
                  <Alert severity="error" dir="rtl">
                    ארעה שגיאה בשליחת הטופס. אנא נסה שוב.
                  </Alert>
                )}
              </Stack>
            </Stack>
          </Box>
        </form>
      </FormProvider>
      {initDataFaild && <RefreshPopup />}
    </>
  )
}

export default MyForm
